import React from 'react'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import {
  AmbulatoryAuthorizationStatus,
  AmbulatoryAuthorizationStatusPlural,
  AmbulatoryAuthorizationStatusSingular
} from 'common/enum/ambulatory-authorization-status'
import { AmbulatoryOrderModel } from 'domain/entities/ambulatory-orders-model'

type AmbulatoryCardProps = {
  order: AmbulatoryOrderModel
  withBorder?: boolean
  withHeight?: boolean
  onClick?: () => void
}

const AmbulatoryCard = ({
  order,
  withBorder,
  withHeight,
  onClick
}: AmbulatoryCardProps) => {
  const ambulatory_status_colors = [
    {
      key: AmbulatoryAuthorizationStatus.HOSPITAL_ANALYSIS,
      label: AmbulatoryAuthorizationStatusPlural.HOSPITAL_ANALYSIS,
      color: 'secondary500',
      background: 'secondary50'
    },
    {
      key: AmbulatoryAuthorizationStatus.ANALYSIS_INSURANCE,
      label: AmbulatoryAuthorizationStatusPlural.ANALYSIS_INSURANCE,
      color: 'secondary500',
      background: 'secondary50'
    },
    {
      key: AmbulatoryAuthorizationStatus.WAITING_BUDGET,
      label: AmbulatoryAuthorizationStatusPlural.WAITING_BUDGET,
      color: 'secondary500',
      background: 'secondary50'
    },
    {
      key: AmbulatoryAuthorizationStatus.DENIED,
      label: AmbulatoryAuthorizationStatusPlural.DENIED,
      color: 'terciary500',
      background: 'terciary50'
    },
    {
      key: AmbulatoryAuthorizationStatus.AUTHORIZED,
      label: AmbulatoryAuthorizationStatusPlural.AUTHORIZED,
      color: 'primary500',
      background: 'primary50'
    },
    {
      key: AmbulatoryAuthorizationStatus.DONE,
      label: AmbulatoryAuthorizationStatusPlural.DONE,
      color: 'primary500',
      background: 'primary50'
    },
    {
      key: AmbulatoryAuthorizationStatus.CANCELED,
      label: AmbulatoryAuthorizationStatusPlural.CANCELED,
      color: 'neutral500',
      background: 'neutral50'
    }
  ]

  function getColorStatus(status: any) {
    const statusColor = ambulatory_status_colors.find(
      (statusOption) => statusOption.key === status
    )

    return statusColor
  }

  const getMainProcedure = (procedures: any) => {
    if (procedures?.length === 1) {
      return procedures[0]?.name
    } else return procedures?.find((procedure: any) => procedure.isMain)?.name
  }

  const getMainMedication = (medications: any) => {
    if (medications?.length === 1) {
      return medications[0]?.name
    } else
      return medications?.find((medication: any) => medication.isMain)?.name
  }

  const getActiveStatus = (statuses: any) => {
    if (statuses?.length === 1) {
      return statuses[0]?.status
    } else return statuses?.find((status: any) => status.isActive)?.status
  }

  return (
    <S.Wrapper
      color={
        withBorder
          ? getColorStatus(getActiveStatus(order?.statuses))?.color
          : 'transparent'
      }
      onClick={onClick && onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <S.Header
        color={getColorStatus(getActiveStatus(order?.statuses))?.background}
      >
        <S.HeaderInfo>
          {order?.orderId && (
            <SupportTextNew color="neutral800" size="small" weight="semiBold">
              Nº {order?.orderId}
            </SupportTextNew>
          )}
          <S.Tag
            color={getColorStatus(getActiveStatus(order?.statuses))?.color}
          >
            {
              AmbulatoryAuthorizationStatusSingular[
                getActiveStatus(
                  order?.statuses
                ) as keyof typeof AmbulatoryAuthorizationStatusSingular
              ]
            }
          </S.Tag>
        </S.HeaderInfo>
        <S.DividerColor
          color={getColorStatus(getActiveStatus(order?.statuses))?.color}
          marginBottom="10px"
        />
      </S.Header>
      <S.CardBody withHeight={withHeight}>
        <S.TextWrapper>
          {getMainProcedure(order.procedures) && (
            <>
              <SupportTextNew
                weight="semiBold"
                color="neutral700"
                size="xxsmall"
              >
                Procedimento
              </SupportTextNew>
              <HeadingNew weight="bold" color="primary900" size="small">
                {getMainProcedure(order.procedures)}
              </HeadingNew>
            </>
          )}

          {getMainMedication(order?.medications) && (
            <>
              <SupportTextNew
                weight="semiBold"
                color="neutral700"
                size="xxsmall"
              >
                Medicação
              </SupportTextNew>
              <HeadingNew weight="bold" color="primary900" size="small">
                {getMainMedication(order?.medications)}
              </HeadingNew>
            </>
          )}

          <SupportTextNew weight="semiBold" color="neutral700" size="xxsmall">
            Paciente
          </SupportTextNew>
          <HeadingNew weight="normal" color="primary900" size="small">
            {order?.patient?.name}
          </HeadingNew>

          <SupportTextNew weight="semiBold" color="neutral700" size="xxsmall">
            Médico
          </SupportTextNew>
          <HeadingNew weight="normal" color="neutral800" size="small">
            {order?.doctor?.name}
          </HeadingNew>

          <SupportTextNew weight="semiBold" color="neutral700" size="xsmall">
            Convênio
          </SupportTextNew>
          <HeadingNew weight="normal" color="neutral800" size="xsmall">
            {order?.healthInsurance?.name}
          </HeadingNew>

          <SupportTextNew weight="semiBold" color="neutral700" size="xxsmall">
            Unidade
          </SupportTextNew>
          <HeadingNew weight="normal" color="neutral800" size="xsmall">
            {order?.hospital?.name}
          </HeadingNew>
        </S.TextWrapper>
      </S.CardBody>
    </S.Wrapper>
  )
}

export default AmbulatoryCard
